import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate} from "react-router-dom";
import {isAuthRedirectError, requireAuth} from "../../shared/services/auth.service";
import {datadogLogs} from '@datadog/browser-logs'
import {logError} from "../../shared/services/logger.service";
import {useToolbar} from "../../components/ToolbarContext";
import {getWorkOrders} from "./work-order.service";
import {ListWithRecord, useRecordRoute} from "../../shared/components/record/RecordKit";
import JobRecordPage from "../estimate/record/JobRecordPage";
import {WorkOrderDto, WorkOrderStatus} from './dtos/work-order.dto';
import {estimateTotalCost} from '../../shared/misc/estimate-helpers';
import {usePermissions} from "../../shared/permissions/PermissionContext";
import {PermissionCode} from "../../shared/permissions/permissions";
import {
  CardSpec,
  ChipDef,
  ListRow,
  MapListPage,
  StatusListPage,
  useStatusList,
} from "../../shared/components/list-page/ListPageKit";
import RecordsMap from "../../shared/components/list-page/RecordsMap";
import {
  addressOneLine,
  displayPhone,
  formatMoney,
  prettyStatusName,
  scheduleListLine,
} from "../../shared/components/list-page/list-format";

// One Paper Model: a job is the "current face" of a record until it is
// invoiced, at which point it advances to the Invoices page. The chips are
// Android's (JobFilters.kt).
const JOB_CHIPS: ChipDef[] = [
  {label: 'Confirmed', urlParam: 'confirmed'},
  {label: 'Scheduled', urlParam: 'scheduled'},
  {label: 'Completed', urlParam: 'completed'},
  {label: 'On Hold', urlParam: 'onHold'},
  {label: 'Canceled', urlParam: 'cancelled'},
];

/**
 * Android's grouping: an in-progress job is being worked, not merely booked,
 * so it files under Confirmed; the invoicing tail (Invoiced, Pending
 * Re-Invoice) under Completed, the work itself being done. Invoiced also
 * covers a job whose invoice was later deleted, which the API returns again.
 */
const chipFor = (status: string | undefined): string | null => {
  switch (status) {
    case WorkOrderStatus.Confirmed:
    case WorkOrderStatus.InProgress:
      return 'Confirmed';
    case WorkOrderStatus.Scheduled:
      return 'Scheduled';
    case WorkOrderStatus.Completed:
    case WorkOrderStatus.Invoiced:
    case WorkOrderStatus.PendingReInvoice:
      return 'Completed';
    case WorkOrderStatus.OnHold:
      return 'On Hold';
    case WorkOrderStatus.Cancelled:
      return 'Canceled';
    default:
      return null;
  }
};

// Jobs that are done (or cancelled) no longer show when they were scheduled, as on Android.
const UNSCHEDULED_STATUSES: string[] = [
  WorkOrderStatus.Completed, WorkOrderStatus.Invoiced, WorkOrderStatus.PendingReInvoice,
  WorkOrderStatus.Cancelled, WorkOrderStatus.Deleted,
];

/*
 * The card's realistic widths, from the Shippy's Tree Service snapshot (488
 * jobs, measured in Chrome with the card's own CSS; 98th percentile, so the
 * longest 2% wrap inside their slot):
 *   name      221px at 18px  "Testing For Status Updates"
 *   phone      99px at 14px  "(208) 631-0182"
 *   address   314px at 14px  "3341 NW 2nd Ave, New Plymouth, ID, Fruitland, ID"
 *   schedule  234px at 14px  "Scheduled Wed, Sep 24th · 10:30 AM", the widest
 *                             the format writes (no job in the snapshot is
 *                             scheduled yet)
 *   total     124px at 14px  "Job total $8,888.88" (the largest real total
 *                             is $8,455; budgeted with cents)
 *   status    125px          the "Pending Re Invoice" pill, the longest status
 * Switch points (cardSwitchPoints): one row from 1,295px, grouped from 766px.
 */
const CARD_SPEC: CardSpec = {
  title: 221,
  status: 125,
  slots: [
    {key: 'phone', group: 'who', width: 99},
    {key: 'address', group: 'details', width: 314},
    {key: 'total', group: 'money', width: 124},
    {key: 'schedule', group: 'details', width: 234},
  ],
};

const JOB_NOUN = {one: 'job', many: 'jobs'};

/**
 * The Jobs page, as on Android: status chips + search, one card per job
 * titled by its client, and the jobs map behind the MAP tab for those who can
 * see every job. No title-row button: a job is only born by approving an
 * estimate.
 */
function WorkOrderListPage() {
  const navigate = useNavigate();
  const toolbarContext = useToolbar();
  const {hasPermission} = usePermissions();
  // SQ-597: a job opens as its own page (/jobs/12) over the list.
  const record = useRecordRoute('/jobs');
  const openRecord = record.open;

  const [workOrders, setWorkOrders] = useState<WorkOrderDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mapOpen, setMapOpen] = useState(false);

  useEffect(() => {
    toolbarContext.initToolbar('WorkOrderListPage', '');
    datadogLogs.logger.info('User navigated to jobs');
    void fetchWorkOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchWorkOrders() {
    setLoading(true);
    setError(null);
    try {
      await requireAuth(navigate);
      const all = await getWorkOrders(navigate);
      // Deleted jobs are gone for good, as on Android.
      setWorkOrders(all.filter(workOrder => workOrder.status !== WorkOrderStatus.Deleted));
    } catch (e) {
      if (isAuthRedirectError(e)) return;
      logError('Error fetching jobs', {}, e as Error);
      setError(`Couldn't load jobs: ${e}`);
    } finally {
      setLoading(false);
    }
  }

  const rows: ListRow[] = useMemo(() => workOrders.map(workOrder => {
    const estimate = workOrder.estimateData;
    // The job site (the estimate's address) beats the client's home address.
    const address = addressOneLine(
      estimate?.address || workOrder.clientAddress,
      estimate?.city || workOrder.clientCity,
      estimate?.state || workOrder.clientState,
    );
    const schedule = UNSCHEDULED_STATUSES.includes(workOrder.status)
      ? undefined
      : scheduleListLine(workOrder.scheduledDate, workOrder.scheduledStartTime);
    // SQ-636: a total hidden from this person (pricesMasked) is simply not shown, on the card or its pin.
    const hidden = estimate?.pricesMasked === true;
    const total = estimate && !hidden ? formatMoney(estimateTotalCost(estimate)) : undefined;
    return {
      key: workOrder.id,
      title: workOrder.clientName?.trim() || `Job #${workOrder.id}`,
      values: {
        phone: displayPhone(workOrder.clientPhoneNumber),
        address,
        total: hidden ? undefined : total
          ? {label: 'Job total', value: total}
          : {label: 'Job total', value: 'Not set', muted: true},
        schedule: schedule ? {label: 'Scheduled', value: schedule} : undefined,
      },
      status: workOrder.status ? prettyStatusName(workOrder.status) : undefined,
      chip: chipFor(workOrder.status),
      onClick: () => openRecord(workOrder.id),
      location: estimate ? {lat: estimate.lat, lng: estimate.lng} : null,
      mapLine: total,
    };
  }), [workOrders, openRecord]);

  const list = useStatusList(rows, JOB_CHIPS);

  return (
    <ListWithRecord
      record={typeof record.id === 'number' && (
        <JobRecordPage key={record.id} workOrderId={record.id} onChanged={() => void fetchWorkOrders()}/>
      )}
      list={(
        <MapListPage
          mapOpen={mapOpen}
          onMapOpenChange={setMapOpen}
          // As on Android, only someone who can see every job gets the map.
          mapAvailable={hasPermission(PermissionCode.JobsViewAll)}
          list={(
            <StatusListPage
              title="Jobs"
              searchLabel="Job Filters and Search"
              list={list}
              spec={CARD_SPEC}
              loading={loading}
              error={error}
              onRetry={() => void fetchWorkOrders()}
            />
          )}
          map={(
            <RecordsMap
              list={list}
              filterLabel="Job Filters"
              noun={JOB_NOUN}
              loading={loading}
              error={error}
              onRetry={() => void fetchWorkOrders()}
            />
          )}
        />
      )}
    />
  );
}

export default WorkOrderListPage;
